import React, { useState, useEffect, useRef } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import DropDownOption from "../DropDownOption/DropDownOption";
import ModuleDefaultHomeSelector from "../ModuleDefaultHome/ModuleDefaultHomeSelector";
import {
  MODULE_DEFAULT_PATH_CHANGED_EVENT,
  getSavedModuleDefaultPath,
  hasUserConfiguredModuleDefault,
} from "@helpers/moduleDefaultPath";
import { confirmPageLeaveIfDirty } from "@helpers/pageLeaveGuard";

function MenuBar({
  sidebarModal,
  setSidebarModal,
  menuBarList,
  routerName,
  pagination,
  permissions,
}) {
  const navigate = useNavigate();
  const location = useLocation();
  const [toggle, setToggle] = useState({});
  const prevRouterNameRef = useRef(null);
  const [defaultHomePath, setDefaultHomePath] = useState(null);
  const [hasConfiguredDefaultHome, setHasConfiguredDefaultHome] =
    useState(false);

  const syncDefaultHome = () => {
    setDefaultHomePath(getSavedModuleDefaultPath(routerName));
    setHasConfiguredDefaultHome(hasUserConfiguredModuleDefault(routerName));
  };

  useEffect(() => {
    syncDefaultHome();
  }, [routerName]);

  useEffect(() => {
    const onChanged = (event) => {
      const key = event.detail?.sidebarName;
      if (key === routerName || String(key || "").startsWith(`${routerName}/`)) {
        syncDefaultHome();
      }
    };
    window.addEventListener(MODULE_DEFAULT_PATH_CHANGED_EVENT, onChanged);
    return () =>
      window.removeEventListener(MODULE_DEFAULT_PATH_CHANGED_EVENT, onChanged);
  }, [routerName]);

  // 進入「設定」區時預設展開「訊息設定」，方便直接看到「自動化訊息設定」
  useEffect(() => {
    if (routerName === "options" && prevRouterNameRef.current !== "options") {
      setToggle((t) => ({ ...t, message: true }));
    }
    prevRouterNameRef.current = routerName;
  }, [routerName]);

  // 自動展開手風琴：子路由時展開對應父層；班表「服務上架」預設展開，除非使用者手動收合
  useEffect(() => {
    if (pagination) {
      setToggle(prevToggle => {
        const newToggle = { ...prevToggle };

        // 設定頁面的訊息設定 - 自動化訊息設定
        if (pagination === 'autoreply') {
          newToggle['message'] = true;
        }

        // 點擊訊息設定時也自動展開（因為會跳轉到第一個子項目）
        if (pagination === 'message') {
          newToggle['message'] = true;
        }

        // 設定：病歷管理（罐頭訊息／療程紀錄模板）— 僅在該分組頁時展開，不預設展開
        if (
          pagination === 'canned-message' ||
          pagination === 'treatment-record-templates' ||
          pagination === 'medical-record'
        ) {
          newToggle['medical-record'] = true;
        }

        // 班表：服務上架（含分類管理、成本與分潤）預設展開；若使用者已手動收合則保留收合
        if (routerName === 'shiftschedule') {
          if (['categorymanagement', 'extraservice', 'service-cost'].includes(pagination)) {
            newToggle['service'] = true;
          } else if (prevToggle['service'] !== false) {
            newToggle['service'] = true;
          }

          // 同意書／問卷：僅在該分組頁時展開，不預設展開
          if (['consent-templates', 'consent-category-link', 'consent-service-link'].includes(pagination)) {
            newToggle['consent-templates'] = true;
          }

          if (['payment-consent-template', 'payment-consent-service-link', 'payment-consent-category-link'].includes(pagination)) {
            newToggle['payment-consent-template'] = true;
          }

          if (['questionnaire-templates', 'questionnaire-service-link', 'questionnaire-category-link'].includes(pagination)) {
            newToggle['questionnaire-templates'] = true;
          }
        }

        return newToggle;
      });
    }
  }, [pagination, routerName]);

  const handleMenuClick = async (e) => {
    if (e === "manual") return;
    const nextPath = `/${routerName}/${e}`;
    const currentPath = (location.pathname || "").replace(/\/+$/, "") || "/";
    const normalizedNext = nextPath.replace(/\/+$/, "") || "/";
    if (currentPath === normalizedNext) return;
    confirmPageLeaveIfDirty(() => navigate(nextPath));
  };

  return (
    <>
      <button
        className={`mini-sidebar-btn-closed mini-sidebar-btn-closed--fixed${
          sidebarModal ? " mini-sidebar-btn-closed--hidden" : ""
        }`}
        onClick={() => setSidebarModal(true)}
      >
        <span className="left"></span>
      </button>
      <div
        className={`menu-bar__sidebarModal light-shadow${
          routerName === "shiftschedule"
            ? " menu-bar__sidebarModal--shiftschedule"
            : ""
        }`}
        style={{ left: sidebarModal ? "0px" : "-100%" }}
      >
        {sidebarModal && (
          <button
            type="button"
            className="mini-sidebar-btn-open"
            onClick={() => setSidebarModal(false)}
          >
            <span className="right"></span>
          </button>
        )}
        <section className="menu-bar__sidebarModal-scroll">
          <div className="menu-bar__sidebarModal-menu">
            <ModuleDefaultHomeSelector
              routerName={routerName}
              menuBarList={menuBarList}
              onDefaultChange={syncDefaultHome}
            />
            {menuBarList.map((item, index) => (
            <DropDownOption
              key={`${item.label}-${index}`}
              list={item}
              handleMenuClick={handleMenuClick}
              toggle={toggle}
              setToggle={setToggle}
              pagination={pagination}
              routerName={routerName}
              defaultHomePath={defaultHomePath}
              showDefaultHomeIndicator={hasConfiguredDefaultHome}
            />
            ))}
          </div>
        </section>
      </div>
    </>
  );
}

export default MenuBar;
